import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, TransitionGroup as _TransitionGroup, withCtx as _withCtx, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-095f810c"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["width", "height", "viewBox"]
const _hoisted_2 = ["x1", "x2", "y1", "y2"]
const _hoisted_3 = ["x1", "x2", "y1", "y2", "stroke-width"]
const _hoisted_4 = ["x", "y"]
const _hoisted_5 = ["cx", "cy"]
const _hoisted_6 = { key: 1 }
const _hoisted_7 = ["cx", "cy"]
const _hoisted_8 = ["cx", "cy"]
const _hoisted_9 = ["cx", "cy", "fill"]
const _hoisted_10 = ["cx", "cy", "fill"]
const _hoisted_11 = ["x", "y"]
const _hoisted_12 = ["x", "y"]
const _hoisted_13 = ["cx", "cy", "fill"]
const _hoisted_14 = ["cx", "cy", "fill"]
const _hoisted_15 = ["x", "y"]
const _hoisted_16 = ["x", "y"]
const _hoisted_17 = ["x", "y", "width", "height", "onClick", "onMouseover", "onMouseout"]

import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import { useFretboardStore } from "@/stores/fretboard";
import { storeToRefs } from "pinia";
import type { FretboardNote } from "@/types";
import TuningControls from "./TuningControls.vue";
import TooltipContainer from "./TooltipContainer.vue";
import FretboardTooltipContent from "./FretboardTooltipContent.vue";
import { noteNames, getScaleDegreeInScale } from "@/utils/noteUtils";
import {
  useFretboardTooltip,
  createNoteTooltipContent,
} from "@/composables/useFretboardTooltip";
import { useSimpleTooltip } from "@/composables/useSimpleTooltip";
import NoteLabel from "./NoteLabel.vue";
import ScaleDegreeLabel from "./ScaleDegreeLabel.vue";
import { useGlobalTooltip } from "@/composables/useGlobalTooltip";

// Component gets isScaleDegree from store - no props needed


export default /*#__PURE__*/_defineComponent({
  __name: 'FretboardDisplay',
  setup(__props) {

const store = useFretboardStore();
const {
  scaleDegreeSettings,
  isScaleDegree,
  numFrets,
  fretboardNotes,
  isGuitar,
  selectedScale,
  hasUsedFretboardRootClick,
} = storeToRefs(store);

// Track notes for visibility (Vue transitions handle timing)
const notesToShow = ref(new Set<string>());

function getZIndex(note: FretboardNote) {
  // Base z-index: lower frets (closer to nut) have higher z-index
  // Fret 0 (open) = 100, fret 1 = 99, fret 2 = 98, etc.
  const baseZIndex = 100 - note.fret;

  return baseZIndex;
}

// Compute which notes should be visible based on current settings
const shouldBeVisible = computed(() => {
  const noteIds = new Set<string>();
  fretboardNotes.value.forEach((note) => {
    const chromaticIndex = note.interval % 12;
    if (scaleDegreeSettings.value[chromaticIndex]?.show) {
      noteIds.add(note.id);
    }
  });
  return noteIds;
});

// Watch for visibility changes and handle exit timing
watch(
  shouldBeVisible,
  (newVisibleIds, oldVisibleIds) => {
    if (!oldVisibleIds) {
      // Initial load - add all visible notes immediately
      newVisibleIds.forEach((id) => notesToShow.value.add(id));
      return;
    }

    // Find notes that should start entering
    const entering = new Set(
      [...newVisibleIds].filter((id) => !oldVisibleIds.has(id))
    );

    // Find notes that should start exiting
    const exiting = new Set(
      [...oldVisibleIds].filter((id) => !newVisibleIds.has(id))
    );

    // Add entering notes immediately
    entering.forEach((id) => {
      notesToShow.value.add(id);
    });

    // Handle exiting notes immediately (let Vue transitions handle timing)
    exiting.forEach((id) => {
      notesToShow.value.delete(id);
    });
  },
  { immediate: true }
);

// Final visible notes list including those currently exiting, sorted by z-index priority
const visibleNotes = computed(() => {
  const filtered = fretboardNotes.value.filter((note) =>
    notesToShow.value.has(note.id)
  );

  // Sort by z-index priority (higher z-index last, so they render on top)
  return filtered.sort((a, b) => {
    const aZIndex = getZIndex(a);
    const bZIndex = getZIndex(b);
    return bZIndex - aZIndex; // Reversed: higher values come last
  });
});

const fretboardContainer = ref<HTMLDivElement | null>(null);
const fretboardSvg = ref<SVGSVGElement | null>(null);
const hoveredNoteId = ref<string | null>(null);

// Initialize fretboard tooltip system
const {
  state: fretboardTooltipState,
  showNoteTooltip,
  hideTooltip: hideFretboardTooltip,
  initializeFretboard,
} = useFretboardTooltip();

// Initialize simple tooltip system for any remaining simple tooltips
const { state: simpleTooltipState } = useSimpleTooltip();

// Global tooltip coordination
const { registerTooltip, unregisterTooltip, showTooltip: showGlobalTooltip, hideTooltip: hideGlobalTooltip } = useGlobalTooltip();

// Simple tooltip functions are no longer needed for TuningControls
// as it now uses ElementTooltip internally

const margin = { top: 48, right: 20, bottom: 40, left: 56 };
const width = ref(1000);
const height = ref(375);

// Configurable padding controls
const stringPadding = ref(24); // Distance from right edge to string ends
const fretPadding = ref(28); // Distance from right edge to 15th fret (allows space for notes)

const updateDimensions = () => {
  if (fretboardContainer.value) {
    width.value = fretboardContainer.value.clientWidth;
    height.value = fretboardContainer.value.clientHeight;
  }
};

onMounted(() => {
  updateDimensions();
  window.addEventListener("resize", updateDimensions);

  // Initialize tooltip system - disable area detection, rely on individual note events
  if (fretboardSvg.value) {
    initializeFretboard();
    // Don't set custom bounds - let individual note hover events control everything
  }
  
  // Register with global tooltip system
  registerTooltip('fretboard-display', hideFretboardTooltipGlobal);
});

// Reinitialize tooltip system when instrument changes
watch(
  () => store.numStrings,
  () => {
    // Small delay to ensure DOM has updated
    setTimeout(() => {
      if (fretboardSvg.value) {
        initializeFretboard();
        // Don't set custom bounds - rely on individual note events
      }
    }, 50);
  }
);

// Update tooltip content when instruction state or root note changes
watch(
  () => hasUsedFretboardRootClick.value,
  () => {
    updateCurrentTooltipContent();
  }
);

// Update tooltip content when root note changes (affects scale degrees)
watch(
  () => store.rootNote,
  () => {
    updateCurrentTooltipContent();
  }
);

// Update tooltip content when scale changes (affects scale degrees)
watch(
  () => selectedScale.value,
  () => {
    updateCurrentTooltipContent();
  }
);

// Helper function to update tooltip content for currently hovered note
function updateCurrentTooltipContent() {
  // If there's a currently hovered note, update its tooltip content
  if (hoveredNoteId.value && fretboardTooltipState.visible) {
    const hoveredNote = fretboardNotes.value.find(
      (note) => note.id === hoveredNoteId.value
    );
    if (hoveredNote) {
      // Always calculate scale degree for tooltip (show in both note and scale degree modes)
      const properScaleDegree = getScaleDegreeInScale(
        hoveredNote.interval,
        selectedScale.value
      );

      const updatedTooltipContent = createNoteTooltipContent({
        noteName: hoveredNote.name,
        scaleDegree: properScaleDegree,
        fret: hoveredNote.fret,
        string: hoveredNote.string,
        instruction: hasUsedFretboardRootClick.value
          ? undefined
          : "Click to change root note",
      });

      // Update the tooltip content directly
      fretboardTooltipState.content = updatedTooltipContent;
    }
  }
}

onUnmounted(() => {
  window.removeEventListener("resize", updateDimensions);
  unregisterTooltip('fretboard-display');
});

const cellHeight = computed(
  () => (height.value - margin.top - margin.bottom) / store.tuning.length
);

const fretPositions = computed(() => {
  const scaleLength = width.value - margin.left - fretPadding.value;
  //const rule = 17.817; // This is closer to the standard rule for fret positioning
  const rule = 17.817; // This is closer to the standard rule for fret positioning

  let totalLength = 0;
  let fretLength = scaleLength;
  for (let i = 0; i < numFrets.value; i++) {
    const fretSpacing = fretLength / rule;
    totalLength += fretSpacing;
    fretLength -= fretSpacing;
  }

  const scalingFactor = scaleLength / totalLength;
  const positions: number[] = [];
  let adjustedFretLength = scaleLength * scalingFactor;

  for (let i = 0; i <= numFrets.value; i++) {
    const adjustedFretSpacing = adjustedFretLength / rule;
    adjustedFretLength -= adjustedFretSpacing;

    if (i === 0) {
      positions.push(margin.left + 48); // 48 = nut offset, added to left margin
    } else {
      positions.push(positions[i - 1] + adjustedFretSpacing);
    }
  }

  return positions;
});

function getX(fret: number) {
  if (fret === 0) {
    // For open strings, position to the left of the nut
    return fretPositions.value[0] - 28; // was originally - cellHeight.value / 2
  } else {
    // For fretted notes, position in the center of the fret
    return (fretPositions.value[fret - 1] + fretPositions.value[fret]) / 2;
  }
}

function getY(string: number) {
  return (
    height.value -
    margin.bottom -
    cellHeight.value * string -
    cellHeight.value / 2
  );
}

function getStringY(index: number) {
  return (
    height.value -
    margin.bottom -
    cellHeight.value * index -
    cellHeight.value / 2
  );
}

function getNoteColor(note: FretboardNote) {
  // Use chromatic interval (0-11) for indexing into the 12-note settings
  const chromaticIndex = note.interval % 12;
  const setting = scaleDegreeSettings.value[chromaticIndex];

  if (!setting || !setting.show) return "transparent";

  if (!setting.color) {
    // Notes shown, not colored: bright = shade-60 bg, dim = shade-30 bg
    return setting.bright ? "var(--shade-60)" : "var(--shade-30)";
  }

  const colorIndex = note.interval % 12;
  return setting.bright
    ? `var(--color-${colorIndex})`
    : `var(--color-${colorIndex}-dimmed)`;
}

function getBackgroundColor(note: FretboardNote) {
  // Use chromatic interval (0-11) for indexing into the 12-note settings
  const chromaticIndex = note.interval % 12;
  const setting = scaleDegreeSettings.value[chromaticIndex];

  if (!setting || !setting.show) return "transparent";

  return "var(--shade-20)";
}

function getTextColor(note: FretboardNote) {
  // Use chromatic interval (0-11) for indexing into the 12-note settings
  const chromaticIndex = note.interval % 12;
  const setting = scaleDegreeSettings.value[chromaticIndex];

  if (!setting || !setting.show) return "transparent";

  if (!setting.color) {
    // Notes shown, not colored: bright = shade-10 text, dim = shade-60 text
    return setting.bright ? "var(--shade-10)" : "var(--shade-60)";
  }

  return setting.bright ? "var(--shade-20)" : "var(--light-48)";
}


//not 100 sure about this
// const isGuitar = computed(() => store.numStrings === 6);

function handleTuningChanged(newTuning: string[]) {
  store.setTuning(newTuning);
}

function handleNoteClick(note: FretboardNote) {
  // Set the clicked note as the new root note and mark that user has used this feature
  store.setRootNoteFromFretboard(note.name);
}

function getInteractiveX(fret: number) {
  if (fret === 0) {
    // Open string: start from left margin
    return margin.left;
  } else {
    // Fretted note: start from previous fret position
    return fretPositions.value[fret - 1];
  }
}

function getInteractiveY(string: number) {
  // Center the interactive area on the string
  return getY(string) - cellHeight.value / 2;
}

function getInteractiveWidth(fret: number) {
  if (fret === 0) {
    // Open string: width from left margin to first fret
    return fretPositions.value[0] - margin.left;
  } else {
    // Fretted note: width between current and previous fret
    return fretPositions.value[fret] - fretPositions.value[fret - 1];
  }
}

function handleNoteHover(note: FretboardNote, isHovered: boolean) {
  if (isHovered) {
    hoveredNoteId.value = note.id;

    if (!fretboardSvg.value) return;

    // Calculate note center coordinates
    const noteCenterX = getX(note.fret);
    const noteCenterY = getY(note.string);

    // Create tooltip content with proper scale degree (always show in tooltip)
    const properScaleDegree = getScaleDegreeInScale(
      note.interval,
      selectedScale.value
    );

    const tooltipContent = createNoteTooltipContent({
      noteName: note.name,
      scaleDegree: properScaleDegree,
      fret: note.fret,
      string: note.string,
      instruction: hasUsedFretboardRootClick.value
        ? undefined
        : "Click to change root note",
    });

    // Show note-anchored tooltip
    showNoteTooltip(
      note.id,
      tooltipContent,
      noteCenterX,
      noteCenterY,
      fretboardSvg.value,
      note.string,
      store.numStrings,
      note.fret,
      store.numFrets
    );
    
    // Register with global tooltip system
    showGlobalTooltip('fretboard-display');
  } else {
    hoveredNoteId.value = null;
    // Hide tooltip after a short delay to allow moving to adjacent notes
    setTimeout(() => {
      // Only hide if we're not hovering over another note
      if (!hoveredNoteId.value) {
        hideFretboardTooltip();
        hideGlobalTooltip('fretboard-display');
      }
    }, 150);
  }
}

function hideFretboardTooltipGlobal() {
  hideFretboardTooltip();
  hoveredNoteId.value = null;
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "fretboard",
    ref_key: "fretboardContainer",
    ref: fretboardContainer
  }, [
    _createVNode(TuningControls, {
      tuning: _unref(store).tuning,
      "num-strings": _unref(store).numStrings,
      "note-names": _unref(noteNames),
      "is-guitar": _unref(isGuitar),
      onTuningChanged: handleTuningChanged
    }, null, 8, ["tuning", "num-strings", "note-names", "is-guitar"]),
    (_openBlock(), _createElementBlock("svg", {
      width: width.value,
      height: height.value,
      viewBox: `0 0 ${width.value} ${height.value}`,
      ref_key: "fretboardSvg",
      ref: fretboardSvg
    }, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(fretPositions.value, (position, index) => {
        return (_openBlock(), _createElementBlock("line", {
          key: `fret-${index}`,
          x1: position,
          x2: position,
          y1: margin.top,
          y2: height.value - margin.bottom,
          class: _normalizeClass({ nut: index === 0, fret: index > 0 })
        }, null, 10, _hoisted_2))
      }), 128)),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(store).tuning, (_, index) => {
        return (_openBlock(), _createElementBlock("line", {
          key: `string-${index}`,
          x1: margin.left,
          x2: width.value - stringPadding.value,
          y1: getStringY(_unref(store).tuning.length - 1 - index),
          y2: getStringY(_unref(store).tuning.length - 1 - index),
          "stroke-width": 1 + index / 1.5,
          class: "string"
        }, null, 8, _hoisted_3))
      }), 128)),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(fretPositions.value.slice(1), (position, index) => {
        return (_openBlock(), _createElementBlock("g", {
          key: `marker-${index}`
        }, [
          _createElementVNode("text", {
            x: (position + fretPositions.value[index]) / 2,
            y: margin.top / 2,
            "alignment-baseline": "middle",
            class: _normalizeClass({
            'fret-number-label': true,
            'fret-number-label--highlighted': [3, 5, 7, 9, 12, 15].includes(
              index + 1
            ),
          })
          }, _toDisplayString(index + 1), 11, _hoisted_4),
          ([3, 5, 7, 9, 15, 17, 19, 21].includes(index + 1))
            ? (_openBlock(), _createElementBlock("circle", {
                key: 0,
                cx: (position + fretPositions.value[index]) / 2,
                cy: height.value - margin.bottom / 2,
                r: "5",
                class: "fret-marker"
              }, null, 8, _hoisted_5))
            : _createCommentVNode("", true),
          (index + 1 === 12)
            ? (_openBlock(), _createElementBlock("g", _hoisted_6, [
                _createElementVNode("circle", {
                  cx: (position + fretPositions.value[index]) / 2 - 8,
                  cy: height.value - margin.bottom / 2,
                  r: "5",
                  class: "fret-marker"
                }, null, 8, _hoisted_7),
                _createElementVNode("circle", {
                  cx: (position + fretPositions.value[index]) / 2 + 8,
                  cy: height.value - margin.bottom / 2,
                  r: "5",
                  class: "fret-marker"
                }, null, 8, _hoisted_8)
              ]))
            : _createCommentVNode("", true)
        ]))
      }), 128)),
      _createVNode(_TransitionGroup, { name: "note-marker" }, {
        default: _withCtx(() => [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleNotes.value, (note) => {
            return (_openBlock(), _createElementBlock("g", {
              key: `position-${note.fret}-${note.string}`,
              class: _normalizeClass(["note-marker", { 'hover-active': hoveredNoteId.value === note.id }]),
              style: _normalizeStyle({ opacity: hoveredNoteId.value === note.id ? 0 : 1 })
            }, [
              _createElementVNode("circle", {
                cx: getX(note.fret),
                cy: getY(note.string),
                r: "24",
                fill: getBackgroundColor(note),
                stroke: "none"
              }, null, 8, _hoisted_9),
              _createElementVNode("circle", {
                cx: getX(note.fret),
                cy: getY(note.string),
                r: "20",
                fill: getNoteColor(note),
                stroke: "none"
              }, null, 8, _hoisted_10),
              (!_unref(isScaleDegree))
                ? (_openBlock(), _createElementBlock("foreignObject", {
                    key: 0,
                    x: getX(note.fret) - 20,
                    y: getY(note.string) - 20,
                    width: "40",
                    height: "40",
                    class: "note-foreign-object"
                  }, [
                    _createElementVNode("div", {
                      class: "note-label-container",
                      style: _normalizeStyle({ color: getTextColor(note), fill: getTextColor(note) })
                    }, [
                      _createVNode(NoteLabel, {
                        "chromatic-index": _unref(noteNames).indexOf(note.name),
                        "display-mode": 'logical',
                        "key-signature": _unref(store).rootNote,
                        size: "medium"
                      }, null, 8, ["chromatic-index", "key-signature"])
                    ], 4)
                  ], 8, _hoisted_11))
                : (_openBlock(), _createElementBlock("foreignObject", {
                    key: 1,
                    x: getX(note.fret) - 20,
                    y: getY(note.string) - 20,
                    width: "40",
                    height: "40",
                    class: "note-foreign-object"
                  }, [
                    _createElementVNode("div", {
                      class: "note-label-container",
                      style: _normalizeStyle({ color: getTextColor(note), fill: getTextColor(note) })
                    }, [
                      _createVNode(ScaleDegreeLabel, {
                        "chromatic-interval": note.interval,
                        scale: _unref(selectedScale),
                        size: "medium"
                      }, null, 8, ["chromatic-interval", "scale"])
                    ], 4)
                  ], 8, _hoisted_12))
            ], 6))
          }), 128))
        ]),
        _: 1
      }),
      (hoveredNoteId.value)
        ? (_openBlock(), _createElementBlock("g", {
            key: `hovered-${hoveredNoteId.value}`,
            class: "note-marker hover-active"
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleNotes.value, (note) => {
              return (_openBlock(), _createElementBlock(_Fragment, {
                key: note.id
              }, [
                (note.id === hoveredNoteId.value)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      _createElementVNode("circle", {
                        cx: getX(note.fret),
                        cy: getY(note.string),
                        r: "24",
                        fill: getBackgroundColor(note),
                        stroke: "none"
                      }, null, 8, _hoisted_13),
                      _createElementVNode("circle", {
                        cx: getX(note.fret),
                        cy: getY(note.string),
                        r: "20",
                        fill: getNoteColor(note),
                        stroke: "none"
                      }, null, 8, _hoisted_14),
                      (!_unref(isScaleDegree))
                        ? (_openBlock(), _createElementBlock("foreignObject", {
                            key: 0,
                            x: getX(note.fret) - 20,
                            y: getY(note.string) - 20,
                            width: "40",
                            height: "40",
                            class: "note-foreign-object"
                          }, [
                            _createElementVNode("div", {
                              class: "note-label-container",
                              style: _normalizeStyle({ color: getTextColor(note), fill: getTextColor(note) })
                            }, [
                              _createVNode(NoteLabel, {
                                "chromatic-index": _unref(noteNames).indexOf(note.name),
                                "display-mode": 'logical',
                                "key-signature": _unref(store).rootNote,
                                size: "medium"
                              }, null, 8, ["chromatic-index", "key-signature"])
                            ], 4)
                          ], 8, _hoisted_15))
                        : (_openBlock(), _createElementBlock("foreignObject", {
                            key: 1,
                            x: getX(note.fret) - 20,
                            y: getY(note.string) - 20,
                            width: "40",
                            height: "40",
                            class: "note-foreign-object"
                          }, [
                            _createElementVNode("div", {
                              class: "note-label-container",
                              style: _normalizeStyle({ color: getTextColor(note), fill: getTextColor(note) })
                            }, [
                              _createVNode(ScaleDegreeLabel, {
                                "chromatic-interval": note.interval,
                                scale: _unref(selectedScale),
                                size: "medium"
                              }, null, 8, ["chromatic-interval", "scale"])
                            ], 4)
                          ], 8, _hoisted_16))
                    ], 64))
                  : _createCommentVNode("", true)
              ], 64))
            }), 128))
          ]))
        : _createCommentVNode("", true),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(fretboardNotes), (note) => {
        return (_openBlock(), _createElementBlock("g", {
          key: `interactive-${note.id}`,
          class: "interactive-marker"
        }, [
          _createElementVNode("rect", {
            x: getInteractiveX(note.fret),
            y: getInteractiveY(note.string),
            width: getInteractiveWidth(note.fret),
            height: cellHeight.value,
            fill: "transparent",
            class: "interactive-area",
            onClick: ($event: any) => (handleNoteClick(note)),
            onMouseover: ($event: any) => (handleNoteHover(note, true)),
            onMouseout: ($event: any) => (handleNoteHover(note, false))
          }, null, 40, _hoisted_17)
        ]))
      }), 128))
    ], 8, _hoisted_1)),
    _createVNode(TooltipContainer, {
      visible: _unref(fretboardTooltipState).visible,
      content: _unref(fretboardTooltipState).content,
      x: _unref(fretboardTooltipState).x,
      y: _unref(fretboardTooltipState).y,
      "is-above": _unref(fretboardTooltipState).isAbove,
      "is-left-side": _unref(fretboardTooltipState).isLeftSide,
      "prevent-hover": true
    }, {
      content: _withCtx(({ data }) => [
        (data && typeof data === 'object' && 'noteName' in data)
          ? (_openBlock(), _createBlock(FretboardTooltipContent, {
              key: 0,
              data: data as any
            }, null, 8, ["data"]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["visible", "content", "x", "y", "is-above", "is-left-side"]),
    _createVNode(TooltipContainer, {
      visible: _unref(simpleTooltipState).visible,
      content: _unref(simpleTooltipState).content,
      x: _unref(simpleTooltipState).x,
      y: _unref(simpleTooltipState).y
    }, null, 8, ["visible", "content", "x", "y"])
  ], 512))
}
}

})